import React, { useState } from 'react';
import {
  Card, Form, Button, Alert, Spinner, Modal, Toast, Badge, Row, Col,
} from 'react-bootstrap';
import { Download, AlertCircle, CheckCircle2, ShieldCheck, Mail, XCircle } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import RequestViewer from './RequestViewer';
import { buildFielFormData } from '../../utils/fielCrypto';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const ComunicadosTab = ({ credentials, isValid }) => {
  const { getToken } = useAuthHeaders();
  const isFiel = credentials.authMode === 'fiel';
  const vaultRegistered = credentials.signingKeyStatus?.isRegistered === true;

  const [descargar, setDescargar]       = useState(true);
  const [loading, setLoading]           = useState(false);
  const [resultado, setResultado]       = useState(null);
  const [errorMsg, setErrorMsg]         = useState('');
  const [showError, setShowError]       = useState(false);
  const [showToast, setShowToast]       = useState(false);
  const [lastRequest, setLastRequest]   = useState(null);

  const [vaultSaving, setVaultSaving]   = useState(false);
  const [vaultSaveOk, setVaultSaveOk]   = useState(false);
  const [vaultSaveErr, setVaultSaveErr] = useState('');


  const downloadBlob = (base64, fileName) => {
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const blob  = new Blob([bytes], { type: 'application/pdf' });
    const url   = URL.createObjectURL(blob);
    const link  = Object.assign(document.createElement('a'), { href: url, download: fileName });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || !isFiel) return;
    setLoading(true);
    setResultado(null);

    try {
      const token  = await getToken();
      const params = new URLSearchParams({ descargar });
      const { formData, headers: fielHeaders } = await buildFielFormData(credentials, token);
      const res = await fetch(`${API_URL_V2}/Consultar/comunicadosfiel?${params}`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Rfc': credentials.rfc, ...fielHeaders },
        body:    formData,
      });

      if (!res.ok) {
        const ct  = res.headers.get('content-type') || '';
        const msg = ct.includes('application/json')
          ? ((await res.json()).errorMessage || `Error ${res.status}`)
          : (await res.text() || `Error ${res.status}`);
        throw new Error(msg);
      }

      const data = await res.json();
      if (!data.success) throw new Error(data.errorMessage || 'No se pudieron obtener los comunicados.');

      setResultado(data);
      setShowToast(true);

      setLastRequest({
        method:      'POST',
        baseUrl:     `${API_URL_V2}/Consultar/comunicadosfiel`,
        queryParams: params.toString(),
        headers:     { 'Rfc': credentials.rfc },
        formFields: vaultRegistered ? null : [
          { name: 'llavePrivada', type: 'file', fileName: credentials.privateKeyFile?.name || 'llave.key',      varName: 'llavePrivadaFile' },
          { name: 'Certificado',  type: 'file', fileName: credentials.certFile?.name     || 'certificado.cer', varName: 'certificadoFile' },
          { name: 'Contrasena',   type: 'text', value: '{contrasena_fiel}' },
        ],
      });

      if (!vaultRegistered && credentials.saveToVault && credentials.onStoreSigningKey) {
        setVaultSaving(true);
        setVaultSaveOk(false);
        setVaultSaveErr('');
        const result = await credentials.onStoreSigningKey();
        setVaultSaving(false);
        if (result.success) setVaultSaveOk(true);
        else setVaultSaveErr(result.error || 'No se pudo guardar la FIEL en Key Vault.');
      }

    } catch (err) {
      setErrorMsg(err.message || 'Error al obtener comunicados.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>

          {!isFiel && (
            <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
              <AlertCircle size={16} className="me-2 flex-shrink-0" />
              <small>Este endpoint solo está disponible con <strong>FIEL</strong>. Cambia el modo de autenticación arriba.</small>
            </Alert>
          )}

          {isFiel && !isValid && (
            <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
              <AlertCircle size={16} className="me-2 flex-shrink-0" />
              <small>Completa las credenciales FIEL de arriba para habilitar la consulta.</small>
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>
            <div className="p-3 mb-3" style={{
              background: 'linear-gradient(135deg,rgba(19,160,255,.05),rgba(0,212,170,.05))',
              borderRadius: 12, border: '1px solid rgba(19,160,255,.12)',
            }}>
              <Form.Label className="form-label-modern mb-2">Modo de descarga</Form.Label>
              <div className="d-flex gap-4">
                <Form.Check type="radio" id="des-true" label="Descargar PDFs"
                  className="custom-radio" checked={descargar === true}
                  onChange={() => setDescargar(true)} />
                <Form.Check type="radio" id="des-false" label="Solo títulos (sin PDF)"
                  className="custom-radio" checked={descargar === false}
                  onChange={() => setDescargar(false)} />
              </div>
              <Form.Text className="text-muted small">
                Con PDFs: descarga el acuse de cada comunicado. Sin PDF: trae solo el listado.
              </Form.Text>
            </div>

            <Button type="submit" className="btn-gradient" disabled={!isValid || !isFiel || loading}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Consultando…</>
                : <><Mail size={18} className="me-2" />Obtener Comunicados</>
              }
            </Button>
          </Form>

          {resultado && (
            <div className="mt-4">
              <ComunicadosList data={resultado} onDownload={downloadBlob} />
            </div>
          )}

          <RequestViewer request={lastRequest} />
        </Card.Body>
      </Card>

      <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide
        className="custom-toast toast-success">
        <Toast.Header>
          <CheckCircle2 size={16} className="me-2 text-success" />
          <strong className="me-auto">Comunicados obtenidos</strong>
        </Toast.Header>
        <Toast.Body>Se encontraron {resultado?.totalComunicados ?? 0} comunicados.</Toast.Body>
      </Toast>

      <Toast show={vaultSaving} className="custom-toast"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header closeButton={false}>
          <Spinner animation="border" size="sm" className="me-2" />
          <strong className="me-auto">Guardando en Key Vault…</strong>
        </Toast.Header>
      </Toast>

      <Toast show={vaultSaveOk} onClose={() => setVaultSaveOk(false)} delay={5000} autohide
        className="custom-toast toast-success"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <ShieldCheck size={14} className="me-2 text-success" />
          <strong className="me-auto">FIEL guardada en Key Vault</strong>
        </Toast.Header>
        <Toast.Body>Ya no necesitarás subir los archivos FIEL para este RFC.</Toast.Body>
      </Toast>

      <Toast show={!!vaultSaveErr} onClose={() => setVaultSaveErr('')} delay={7000} autohide
        className="custom-toast toast-error"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <AlertCircle size={14} className="me-2 text-danger" />
          <strong className="me-auto">No se pudo guardar en Key Vault</strong>
        </Toast.Header>
        <Toast.Body style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vaultSaveErr}</Toast.Body>
      </Toast>

      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />Error al obtener comunicados
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errorMsg}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ComunicadosList = ({ data, onDownload }) => (
  <div>
    <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
      <strong style={{ color: '#1a1f3a' }}>
        <Mail size={16} className="me-1" />
        {data.totalComunicados} comunicado{data.totalComunicados !== 1 ? 's' : ''}
      </strong>
      {data.totalArchivosDescargados > 0 && (
        <Badge bg="success" style={{ fontWeight: 400 }}>
          {data.totalArchivosDescargados} PDF{data.totalArchivosDescargados !== 1 ? 's' : ''} descargado{data.totalArchivosDescargados !== 1 ? 's' : ''}
        </Badge>
      )}
    </div>

    {data.comunicados?.length === 0 && (
      <Alert variant="info" className="py-2 mb-0">
        <small>No se encontraron comunicados para este RFC.</small>
      </Alert>
    )}

    <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      {data.comunicados?.map((c, i) => (
        <div key={c.id || i} style={{
          background: '#fff', borderRadius: 10, padding: '.75rem 1rem',
          border: `1px solid ${c.descargaExitosa === false ? 'rgba(255,107,107,.3)' : 'rgba(0,212,170,.2)'}`,
          display: 'flex', alignItems: 'flex-start', gap: '.75rem',
        }}>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
              {c.esLeido
                ? <CheckCircle2 size={14} color="#adb5bd" />
                : <CheckCircle2 size={14} color="#00d4aa" />
              }
              <span style={{ fontWeight: 500, fontSize: '.85rem', color: '#1a1f3a' }}>
                {c.titulo || '(sin título)'}
              </span>
              {!c.esLeido && <Badge bg="primary" style={{ fontSize: '.7rem', fontWeight: 400 }}>Nuevo</Badge>}
              {c.descargaExitosa === false && (
                <Badge bg="danger" style={{ fontSize: '.7rem', fontWeight: 400 }}>Error PDF</Badge>
              )}
            </div>
            {c.fechaComunicado && (
              <small className="d-block" style={{ fontSize: '.75rem', color: '#6c757d' }}>{c.fechaComunicado}</small>
            )}
            {c.errorDescarga && (
              <small className="text-danger d-block" style={{ fontSize: '.75rem' }}>{c.errorDescarga}</small>
            )}
          </div>
          {c.fileContent && c.descargaExitosa !== false && (
            <Button variant="outline-primary" size="sm"
              className="flex-shrink-0 d-flex align-items-center gap-1"
              style={{ fontSize: '.75rem', whiteSpace: 'nowrap' }}
              onClick={() => onDownload(c.fileContent, c.fileName || `comunicado-${c.id || i}.pdf`)}>
              <Download size={13} />PDF
            </Button>
          )}
        </div>
      ))}
    </div>
  </div>
);

export default ComunicadosTab;
